import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {APPROVAL_STATUS, APPROVAL_TYPE, ApprovalStatus} from '../../../../constants';
import {trackEvent} from './../../../../tracking/amplitude/TrackingV2';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import Moment from 'moment';
import Util from '../../../shared/util/util';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import ForecastTooltip from '../../../shared/components/tooltips/ForecastTooltip';
import {createToast} from '../../../shared/components/toasts/another-toast/toaster';
import UpdateApprovalStatusMutation from '../../../../mutations/UpdateApprovalStatusMutation';
import {showApprovalErrorModal} from '../../../../components/timesheets/time-approval/ApprovalErrors';
import {ConfirmationModalPrimaryButton} from '../../../shared/components/modals/general/ConfirmationModal';

const submitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	lockedDate,
	lastWorkingDayInWeek,
	usingTimeOffApproval,
}) => {
	trackEvent('Submit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	const today = Moment();
	const submitDate = !isWeekView
		? currentViewingDate
		: today.isSame(currentViewingDate, 'week')
		? today
		: lastWorkingDayInWeek;
	showModal({
		type: MODAL_TYPE.SUBMIT_TIME_APPROVAL,
		selectedPersonName: nonViewerPersonSelected ? selectedPerson.firstName : undefined,
		selectedPersonId: selectedPerson.id,
		currentViewingDate: submitDate,
		lockedDate,
		usingTimeOffApproval,
	});
};

export type TimesheetApprovalActions = {
	showSubmit: boolean;
	showResubmit: boolean;
	showUnsubmit: boolean;
};

/**
 * Decide which approval action the period offers.
 *
 * Under the new statuses the period offers exactly one, and the period's status alone chooses
 * it — so the badge the owner reads and the button they press always name the same next step:
 *
 *   pending             → Submit
 *   submitted           → Unsubmit
 *   declined            → Resubmit
 *   approved, invoiced  → nothing; the period is settled
 *
 * A second button is not needed to reach outstanding time, because the status hierarchy that
 * feeds this ranks a period by what it still asks of its owner rather than by how far it has
 * got (see getApprovalStatusForWeek): a period holding unsubmitted rows reads pending even when
 * some of it has already gone to an approver, so Submit is what it offers. A declined period
 * resolves in two steps — Resubmit sends the declined rows back, after which anything added
 * while it was declined leaves the period reading pending, and Submit picks that up.
 *
 * `hasSubmittableTimeRegs` therefore has no say here; it is read only without the new statuses,
 * where there is no pending status for the hierarchy to rank and the choice stays lock-driven.
 *
 * A company that approves time off but **not** timesheets is a supported configuration — the
 * time-off toggle is not nested under Timesheet approval the way Internal time is — and it gets
 * a narrower answer. None of the timesheet verbs have scope there: project and internal rows are
 * born approved, so Submit and Unsubmit have nothing to act on. Resubmit is the exception. An
 * approver can still decline time off, and submitting deliberately refuses a declined row, so
 * without Resubmit that time off has no route back to its approver and its owner is stuck with
 * a decline they cannot answer.
 *
 * @param periodApprovalStatus  aggregate status of the viewed day or week
 * @param hasSubmittableTimeRegs whether any row in the period is still awaiting submission
 * @param isWorkdaysLocked      whether the submission lock covers the period's working days
 * @param usingTimeApproval     whether the company puts timesheets through approval at all
 */
export const getTimesheetApprovalActions = ({
	periodApprovalStatus,
	hasSubmittableTimeRegs,
	hasNewStatuses,
	isWorkdaysLocked,
	usingTimeApproval,
}: {
	periodApprovalStatus: string | null;
	hasSubmittableTimeRegs: boolean;
	hasNewStatuses: boolean;
	isWorkdaysLocked: boolean;
	usingTimeApproval: boolean;
}): TimesheetApprovalActions => {
	if (!usingTimeApproval) {
		// Time off is the only thing under approval here, and "Submit Time Off Only" already
		// covers sending it on. Resubmit is the one verb that surface cannot stand in for.
		return {
			showSubmit: false,
			showResubmit: hasNewStatuses && periodApprovalStatus === APPROVAL_STATUS.DECLINED,
			showUnsubmit: false,
		};
	}

	if (!hasNewStatuses) {
		// Lock-driven rather than status-driven, as it has always been — but with the same
		// "still something to submit" escape, since a legacy client sees rows written under the
		// new statuses as unsubmitted and needs a way to send them on.
		//
		// No DECLINED case to consider: without the flag the read side projects that status to
		// NOT_SUBMITTED, so it never reaches this branch.
		const showUnsubmit = isWorkdaysLocked;
		return {showSubmit: !showUnsubmit || hasSubmittableTimeRegs, showResubmit: false, showUnsubmit};
	}

	switch (periodApprovalStatus) {
		case APPROVAL_STATUS.APPROVED:
		case APPROVAL_STATUS.INVOICED:
			return {showSubmit: false, showResubmit: false, showUnsubmit: false};
		case APPROVAL_STATUS.DECLINED:
			return {showSubmit: false, showResubmit: true, showUnsubmit: false};
		case APPROVAL_STATUS.SUBMITTED:
			return {showSubmit: false, showResubmit: false, showUnsubmit: true};
		default:
			// Pending, and the legacy unsubmitted status the read side projects onto it.
			return {showSubmit: true, showResubmit: false, showUnsubmit: false};
	}
};

/**
 * Compute the date range a Resubmit click should target.
 *
 * A week with no time registrations at all is settled by a week-scoped person-week-approval
 * row, and the back end's guard for that path only recognises a full-week range — so such a
 * week always operates at week granularity regardless of view mode.
 *
 * Otherwise: Week view = full-week range, Day view = single-day range (partial-day resubmit
 * preserved). The predicate spans every category of time, not just project time: a week
 * holding only internal time or time off has rows for the transition to act on, so it does
 * not need — and should not get — the whole-week widening.
 */
export const getResubmitDateRange = (
	currentViewingDate: Moment.Moment,
	isWeekView: boolean,
	hasTimeRegsInWeek: boolean
): {rangeStart: Moment.Moment; rangeEnd: Moment.Moment} => {
	const useWeekRange = isWeekView || !hasTimeRegsInWeek;
	return {
		rangeStart: useWeekRange ? currentViewingDate.clone().startOf('week') : currentViewingDate.clone(),
		rangeEnd: useWeekRange ? currentViewingDate.clone().endOf('week') : currentViewingDate.clone(),
	};
};

/**
 * The mutation input for a Resubmit click.
 *
 * `includeTimeOff` is not optional here. Internal time is in scope server-side wherever the
 * company approves it, but time off is opt-in per request — omit the flag and declined time
 * off is filtered out of the transition and stays declined, with no other action able to
 * move it (submitting deliberately does not accept a declined row; resubmitting is the verb
 * that records the correction).
 */
export const getResubmitInput = ({
	personId,
	currentViewingDate,
	isWeekView,
	hasTimeRegsInWeek,
}: {
	personId: string;
	currentViewingDate: Moment.Moment;
	isWeekView: boolean;
	hasTimeRegsInWeek: boolean;
}) => {
	const {rangeStart, rangeEnd} = getResubmitDateRange(currentViewingDate, isWeekView, hasTimeRegsInWeek);
	return {
		personIds: [personId],
		approvalType: APPROVAL_TYPE.RESUBMIT,
		includeTimeOff: true,
		startYear: rangeStart.year(),
		startMonth: rangeStart.month() + 1,
		startDay: rangeStart.date(),
		endYear: rangeEnd.year(),
		endMonth: rangeEnd.month() + 1,
		endDay: rangeEnd.date(),
	};
};

const resubmitTimesheets = ({nonViewerPersonSelected, selectedPerson, isWeekView, currentViewingDate, hasTimeRegsInWeek}) => {
	trackEvent('Resubmit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	Util.CommitMutation(
		UpdateApprovalStatusMutation,
		getResubmitInput({personId: selectedPerson.id, currentViewingDate, isWeekView, hasTimeRegsInWeek}),
		// RESUBMIT is one of the actions the back end rejects without the new-statuses
		// feature flag. It reports that in `errors` rather than by failing, so with no success
		// handler at all the button did nothing and said nothing.
		res => showApprovalErrorModal(res.updateApprovalStatus?.errors)
	);
};

const unsubmitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	lockedDate,
	companyLockedDate,
}) => {
	trackEvent('Unsubmit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	const unsubmitDate = !isWeekView ? currentViewingDate : currentViewingDate.clone().startOf('week');
	showModal({
		type: MODAL_TYPE.UNSUBMIT_TIME_APPROVAL,
		selectedPersonName: nonViewerPersonSelected ? selectedPerson.firstName : undefined,
		selectedPersonId: selectedPerson.id,
		currentViewingDate: unsubmitDate,
		companyLockedDate,
		lockedDate,
	});
};

type TimesheetsApprovalButtonsProps = {
	selectedPerson; // TimesheetsPage_viewer['company']['person']
	nonViewerPersonSelected: boolean;
	usingTimeApproval: boolean;
	usingTimeOffApproval: boolean;
	isWeekView: boolean;
	currentViewingDate: Moment.Moment;
	lockedDate?: Moment.Moment;
	companyLockedDate?: Moment.Moment;
	lastWorkingDayInWeek;
	periodTimeApprovalStatus?: ApprovalStatus;
	hasTimeRegsInWeek: boolean;
	hasSubmittableTimeRegsInPeriod: boolean;
};
export const TimesheetsApprovalButtons = ({
	selectedPerson,
	nonViewerPersonSelected,
	usingTimeApproval,
	usingTimeOffApproval,
	isWeekView,
	currentViewingDate,
	lockedDate,
	companyLockedDate,
	lastWorkingDayInWeek,
	periodTimeApprovalStatus,
	hasTimeRegsInWeek,
	hasSubmittableTimeRegsInPeriod,
}: TimesheetsApprovalButtonsProps): JSX.Element | null => {
	const {formatMessage} = useIntl();

	const handleConfirmTimeOff = (selectedPersonId: number) => {
		trackEvent('Submit Time Off Modal Save Button', 'Clicked');
		const onSuccess = res => {
			// A refusal comes back in `errors` with `persons` null — which the unguarded
			// `persons[0]` below used to turn into a TypeError, losing the message entirely.
			if (showApprovalErrorModal(res.updateApprovalStatus?.errors, true)) return;
			const personRes = res.updateApprovalStatus?.persons?.[0];
			if (!personRes) return;
			// Report what the mutation actually did, not merely that it ran. A request that
			// matches nothing comes back successful with an empty `timeRegs`, and claiming
			// success there sent owners looking for a state change that never happened.
			//
			// Which rows are submittable depends on the flag (`submittableStatusClause`,
			// TimeApprovalLogic.java:105-109): under the new statuses only time off awaiting
			// submission qualifies, so a declined row does not move and Resubmit is the verb
			// that does it — that is the empty case this exists for. Without the flag DECLINED
			// is in the clause, so Submit does still move declined time off there and this
			// stays a plain success. The toast is not flag-gated because it reports the result
			// either way.
			//
			// The count answers "did the sweep move anything", not "did this period change".
			// `handleSubmitTimeOffFetch` (TimeApprovalLogic.java:151) hardcodes
			// LocalDate.MIN..MAX — deliberately, so one click clears a backlog — and the input
			// below sends no range to narrow it. So a person with any submittable time off
			// anywhere, a future PTO booking included, gets the success message even if the
			// declined row in view is untouched. Bounding the query needs its own design pass
			// (see the `submittableStatusClause` docstring); do not read this count as
			// period-scoped.
			const submittedCount = res.updateApprovalStatus?.timeRegs?.length ?? 0;
			createToast({
				duration: 2500,
				message: formatMessage({
					id: submittedCount
						? 'time_approval.submit_time_off_toast'
						: 'time_approval.submit_time_off_nothing_to_submit_toast',
				}),
			});
		};

		Util.CommitMutation(
			UpdateApprovalStatusMutation,
			{
				personIds: [selectedPersonId],
				approvalType: APPROVAL_TYPE.SUBMIT_TIME_OFF,
				includeTimeOff: true,
			},
			onSuccess
		);
	};

	const handleCloseTimeOffModal = () => {
		trackEvent('Submit Time Off Modal Cancel Button', 'Clicked');
	};

	const openSubmitTimeOffModal = ({nonViewerPersonSelected, selectedPerson}) => {
		trackEvent('Submit Time Off Bottom', 'Clicked', {
			timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
		});

		showModal({
			type: MODAL_TYPE.CONFIRMATION,
			title: formatMessage({id: 'time_approval.submit_time_off'}),
			description: formatMessage({id: 'time_off_approval.submit_timesheets_info'}),
			autoClosePositive: true,
			autoCloseNegative: true,
			callbackPositive: () => handleConfirmTimeOff(selectedPerson.id),
			callbackNegative: handleCloseTimeOffModal,
			includeDefaultMessage: false,
			confirmationModalPrimaryButton: ConfirmationModalPrimaryButton.SAVE,
		});
	};

	const isCompanyLocked = useMemo(() => {
		if (companyLockedDate) {
			return isWeekView
				? companyLockedDate.isSameOrAfter(currentViewingDate.clone().endOf('week'), 'd')
				: companyLockedDate.isSameOrAfter(currentViewingDate, 'd');
		}
		return false;
	}, [isWeekView, companyLockedDate, currentViewingDate]);

	const isWorkdaysLocked = useMemo(() => {
		if (lockedDate && lastWorkingDayInWeek) {
			return lockedDate.isSameOrAfter(lastWorkingDayInWeek, 'd');
		}
		return false;
	}, [isWeekView, lockedDate, lastWorkingDayInWeek]);

	const messageIdSuffix = (usingTimeOffApproval ? '_and_time_off' : '') + '_info';

	if (!usingTimeApproval && !usingTimeOffApproval) return null;

	const hasNewStatuses = hasFeatureFlag('timesheet_approval_new_statuses');

	const {showSubmit, showResubmit, showUnsubmit} = getTimesheetApprovalActions({
		periodApprovalStatus: periodTimeApprovalStatus ?? null,
		hasSubmittableTimeRegs: hasSubmittableTimeRegsInPeriod,
		hasNewStatuses,
		isWorkdaysLocked,
		usingTimeApproval,
	});

	const resubmitButton = (
		<Button
			emphasis={EMPHASIS.HIGH}
			onPress={() =>
				resubmitTimesheets({
					nonViewerPersonSelected,
					selectedPerson,
					isWeekView,
					currentViewingDate,
					hasTimeRegsInWeek,
				})
			}
			data-cy={'resubmit-timesheets-button'}
		>
			{nonViewerPersonSelected
				? formatMessage({id: 'time_approval.resubmit_names_timesheets'}, {name: selectedPerson.firstName})
				: formatMessage({id: 'time_approval.resubmit_timesheets'})}
		</Button>
	);

	return usingTimeApproval ? (
		<>
			{usingTimeOffApproval && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_off_approval.submit_names_timesheets_info',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_off_approval.submit_timesheets_info',
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.MONO}
						onPress={() =>
							openSubmitTimeOffModal({
								nonViewerPersonSelected,
								selectedPerson,
							})
						}
						// Same rule as the timesheet Submit below: time off submits through the
						// same mutation branch, which resolves its locked date without a person,
						// so only a company lock can stop it.
						disabled={isCompanyLocked}
						data-cy={'submit-time-off-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_time_off',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_time_off',
							  })}
					</Button>
				</ForecastTooltip>
			)}
			{showResubmit && resubmitButton}
			{showSubmit && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_timesheets' + messageIdSuffix,
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_timesheets' + messageIdSuffix,
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={() =>
							submitTimesheets({
								nonViewerPersonSelected,
								selectedPerson,
								isWeekView,
								currentViewingDate,
								lockedDate,
								lastWorkingDayInWeek,
								usingTimeOffApproval,
							})
						}
						// Only a company lock can stop a submit. The owner's own submit lock must not:
						// it covers precisely the period whose outstanding time this button exists
						// to send on, so gating on it renders the button inert exactly when it is
						// needed. The server agrees — SUBMIT resolves its locked date without a
						// person, so it enforces the company lock alone.
						disabled={isCompanyLocked}
						data-cy={'submit-timesheets-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_timesheet',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_timesheet',
							  })}
					</Button>
				</ForecastTooltip>
			)}
			{showUnsubmit && (
				// Steps down to secondary on the one path that can still offer two actions at once:
				// without the new statuses the choice is lock-driven, and a locked period that
				// holds unsubmitted rows offers Submit as well.
				<Button
					emphasis={showSubmit ? EMPHASIS.MEDIUM : EMPHASIS.HIGH}
					onPress={() =>
						unsubmitTimesheets({
							nonViewerPersonSelected,
							selectedPerson,
							isWeekView,
							currentViewingDate,
							lockedDate,
							companyLockedDate,
						})
					}
					disabled={isCompanyLocked}
					data-cy={'unsubmit-timesheets-button'}
				>
					{nonViewerPersonSelected
						? formatMessage(
								{
									id: 'time_approval.unsubmit_names_timesheets',
								},
								{
									name: selectedPerson.firstName,
								}
						  )
						: formatMessage({
								id: 'time_approval.unsubmit_timesheets',
						  })}
				</Button>
			)}
		</>
	) : (
		<>
			{showResubmit && resubmitButton}
			{usingTimeOffApproval && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_off_approval.submit_names_timesheets_info',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_off_approval.submit_timesheets_info',
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={() =>
							openSubmitTimeOffModal({
								nonViewerPersonSelected,
								selectedPerson,
							})
						}
						// Same rule as the timesheet Submit below: time off submits through the
						// same mutation branch, which resolves its locked date without a person,
						// so only a company lock can stop it.
						disabled={isCompanyLocked}
						data-cy={'submit-time-off-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_time_off',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_time_off',
							  })}
					</Button>
				</ForecastTooltip>
			)}
		</>
	);
};
